import React from "react";
import RippleEffect from "../../RippleEffect/RippleEffect";
import styled from "styled-components";
import { ITheme } from "../../Themes/ThemeTypes";
import { RippleEffectShapeEnum } from "../../RippleEffect/RippleEffectShapeEnum";
import Icon from "../../Icon/Icon";
import { ThemeColorEnum, ThemeIconEnum } from "../../Themes/ThemeEnums";
import { FilledButtonSizeEnum } from "./FilledButtonEnums";

interface SizeSpec {
  height: string;
  paddingInline: string;
  fontSize: string;
}

const SIZE_SPECS: Record<FilledButtonSizeEnum, SizeSpec> = {
  [FilledButtonSizeEnum.SMALL]: { height: "40px", paddingInline: "16px", fontSize: "14px" },
  [FilledButtonSizeEnum.MEDIUM]: { height: "56px", paddingInline: "24px", fontSize: "16px" },
  [FilledButtonSizeEnum.LARGE]: { height: "96px", paddingInline: "48px", fontSize: "24px" },
  [FilledButtonSizeEnum.EXTRA_LARGE]: { height: "136px", paddingInline: "64px", fontSize: "32px" },
};

const Button = styled.button<{ theme: ITheme; $isDisabled?: boolean; $size: FilledButtonSizeEnum }>`
  display: inline-flex;
  flex: auto;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-inline-size: 64px;
  border: none;
  outline: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  user-select: none;
  appearance: none;
  vertical-align: middle;
  background: ${(props) =>
    props.$isDisabled
      ? `rgba(${props.theme.colors.on_surface}, ${props.theme.base.opacity.disabled_container})`
      : `rgb(${props.theme.colors.primary})`};
  text-decoration: none;
  position: relative;
  z-index: ${(props) => props.theme.base.z_index.base};
  height: ${(props) => SIZE_SPECS[props.$size].height};
  font-family: ${(props) => props.theme.base.typography.font};
  font-size: ${(props) => SIZE_SPECS[props.$size].fontSize};
  font-style: normal;
  font-weight: 500;
  color: ${(props) =>
    props.$isDisabled
      ? `rgba(${props.theme.colors.on_surface}, ${props.theme.base.opacity.disabled_content})`
      : `rgb(${props.theme.colors.on_primary})`};
  gap: 8px;
  padding-inline-start: ${(props) => SIZE_SPECS[props.$size].paddingInline};
  padding-inline-end: ${(props) => SIZE_SPECS[props.$size].paddingInline};
  border-start-start-radius: ${(props) => props.theme.base.button.radius};
  border-start-end-radius: ${(props) => props.theme.base.button.radius};
  border-end-start-radius: ${(props) => props.theme.base.button.radius};
  border-end-end-radius: ${(props) => props.theme.base.button.radius};
  line-height: ${(props) => SIZE_SPECS[props.$size].height};
  overflow: hidden;
  white-space: nowrap;
  cursor: ${(props) => (props.$isDisabled ? "not-allowed" : "pointer")};

  &:before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgb(${(props) => props.theme.colors.on_primary});
    border-start-start-radius: ${(props) => props.theme.base.button.radius};
    border-start-end-radius: ${(props) => props.theme.base.button.radius};
    border-end-start-radius: ${(props) => props.theme.base.button.radius};
    border-end-end-radius: ${(props) => props.theme.base.button.radius};
    opacity: 0;
    transition:
      opacity ${(props) => props.theme.base.motion.duration.short4}ms
        ${(props) => props.theme.base.motion.easing.standard},
      background-color ${(props) => props.theme.base.motion.duration.short4}ms
        ${(props) => props.theme.base.motion.easing.standard};
  }

  &:hover {
    box-shadow: ${(props) =>
      props.$isDisabled ? "none" : props.theme.base.elevation.level1_shadow};

    &:before {
      opacity: ${(props) => (props.$isDisabled ? 0 : props.theme.base.opacity.hover_state_layer)};
    }
  }
`;

interface FilledButtonProps {
  label: string;
  icon?: ThemeIconEnum;
  disabled?: boolean;
  size?: FilledButtonSizeEnum;
  onClick?: () => void;
}

const FilledButton: React.FC<FilledButtonProps> = ({
  label,
  icon,
  disabled,
  size = FilledButtonSizeEnum.SMALL,
  onClick,
}) => {
  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  return (
    <RippleEffect shape={RippleEffectShapeEnum.BUTTON}>
      <Button $isDisabled={disabled} $size={size} onClick={handleClick}>
        {icon !== undefined && <Icon icon={icon} color={ThemeColorEnum.ON_PRIMARY} />}
        {label}
      </Button>
    </RippleEffect>
  );
};

export default FilledButton;
